// Import React elements
import { useMemo, useState } from "react";


// Define the popular social settings in the order they should appear
const popularSocials = [

    // Facebook
    {
        code: "social_facebook",
    },
    // Instagram
    {
        code: "social_instagram",
    },  
    // Tiktok
    {
        code: "social_tiktok",
    },
    // Twitter
    {
        code: "social_twitter",
    },
    // YouTube
    {
        code: "social_youtube",
    },
];



// Render the store socials settings
const RenderConnectionSocials = ({
    formValues,
    addingNewConnection,
    handleSettingsChange,
}) => {

    // Track whether the advanced social settings should be displayed
    const [showAdvancedSocials, setShowAdvancedSocials] = useState(false);



    // Get the current saved value for a social setting
    const getSocialSettingValue = (settingCode) => {

        // Return an empty string when settings is not an array
        if (!Array.isArray(formValues?.settings)) {

            // Return a safe fallback value
            return "";

        }



        // Find the matching setting by its code
        const matchingSetting = formValues.settings.find((setting) => {

            // Return whether this is the requested setting
            return setting?.code === settingCode;

        });



        // Return the current value or an empty string
        return matchingSetting?.value ?? "";

    };



    // Handle a change to a social setting
    const handleSocialSettingChange = (settingCode, nextValue) => {

        // Reuse the existing settings handler with a synthetic event
        handleSettingsChange("Social", settingCode)({

            // Provide the event target expected by the existing handler
            target: {

                // Treat every social setting as a text input
                type: "text",

                // Pass the updated setting value
                value: nextValue,

            },

        });

    };



    // Get all available social settings
    const socialSettings = useMemo(() => {

        // Return an empty array if settings is not available
        if (!Array.isArray(formValues?.settings)) {

            // Return a safe fallback array
            return [];

        }



        // Return only Social settings
        return formValues.settings.filter((setting) => {

            // Check whether this setting belongs to the Social type
            return setting?.type === "Social";

        });

    }, [formValues?.settings]);



    // Get the popular social settings in the defined order
    const popularSocialSettings = useMemo(() => {

        // Map through the popular social definitions
        return popularSocials
            .map((popularSocial) => {

                // Find the matching social setting
                return socialSettings.find((setting) => {

                    // Match by setting code
                    return setting?.code === popularSocial.code;

                });

            })
            .filter(Boolean);

    }, [socialSettings]);



    // Get all non-popular social settings in alphabetical order
    const advancedSocialSettings = useMemo(() => {

        // Create a set of popular social setting codes
        const popularSocialCodes = new Set(
            popularSocials.map((popularSocial) => {

                // Return the popular social code
                return popularSocial.code;

            })
        );



        // Filter out popular settings and sort the remaining settings alphabetically
        return socialSettings
            .filter((setting) => {

                // Keep settings that are not in the popular list
                return !popularSocialCodes.has(setting?.code);

            })
            .sort((settingA, settingB) => {

                // Get the first setting name
                const settingAName = settingA?.name || "";

                // Get the second setting name
                const settingBName = settingB?.name || "";



                // Sort names alphabetically
                return settingAName.localeCompare(settingBName);

            });

    }, [socialSettings]);



    // Render one reusable social setting row
    const renderSocialSetting = (setting) => {

        // Get the current value for this setting
        const currentValue = getSocialSettingValue(setting.code);



        // Render the editable setting row
        return (

            // Use the setting code as the stable React key
            <div
                className="social-settings__item"
                key={setting.code}
                title={setting.description}
            >

                {/* Render the input label */}
                <label
                    className="social-settings__label"
                    htmlFor={setting.code}
                >
                    {setting.name}
                </label>



                {/* Render the editable social value */}
                <input
                    className="social-settings__input"
                    id={setting.code}
                    name={setting.code}
                    type={setting.value_type || "text"}
                    value={currentValue}
                    placeholder={`Enter ${setting.description}`}
                    autoComplete="off"
                    onChange={(event) => {

                        // Send the updated value to the settings handler
                        handleSocialSettingChange(
                            setting.code,
                            event.target.value
                        );

                    }}
                />

            </div>

        );

    };



    // Render the socials card
    return (

        // Wrap the complete socials section
        <div className="card">

            {/* Render the card header */}
            <div className="card__header connection-details-card__header">

                {/* Render the card title */}
                <h3>
                    Socials
                </h3>



                {/* Render the help text */}
                <span className="hintText">

                    {/* Only show the help text for an existing connection */}
                    {!addingNewConnection &&

                        // Explain the expected social value format
                        <span>
                            Enter usernames, handles, invite codes, IDs, or paths rather than full URLs
                        </span>

                    }

                </span>

            </div>



            {/* Render the card content */}
            <div className="card__content">

                {/* Tell the user they need to save the store before socials become available */}
                {addingNewConnection &&

                    // Render the save notice
                    <div>

                        {/* Render the save notice text */}
                        <p>
                            Click save to view and edit socials.
                        </p>

                    </div>

                }



                {/* Render editable socials for an existing connection */}
                {!addingNewConnection &&

                    // Wrap all social controls
                    <div className="social-settings">

                        {/* Render the popular social settings first */}
                        <div className="social-settings__list">

                            {/* Map through the popular social settings */}
                            {popularSocialSettings.map((setting) => {

                                // Render this social setting
                                return renderSocialSetting(setting);

                            })}

                        </div>



                        {/* Only render the additional social settings if they exist */}
                        {advancedSocialSettings.length > 0 &&

                            // Wrap the optional social settings and More / Less control
                            <div className="social-settings__advanced">

                                {/* Render the remaining social settings when expanded */}
                                {showAdvancedSocials &&

                                    // Wrap the additional social settings
                                    <div className="social-settings__list social-settings__list--advanced">

                                        {/* Map through the alphabetically sorted additional settings */}
                                        {advancedSocialSettings.map((setting) => {

                                            // Render this social setting
                                            return renderSocialSetting(setting);

                                        })}

                                    </div>

                                }


                                {/* Keep the More / Less control at the bottom of the card */}
                                <div className="social-settings__more-row">

                                    <button
                                        type="button"
                                        className="social-settings__more-button"
                                        onClick={() => {

                                            // Toggle whether the additional socials are displayed
                                            setShowAdvancedSocials(!showAdvancedSocials);

                                        }}
                                    >
                                        {showAdvancedSocials ? "Less" : "More"}
                                    </button>

                                </div>

                            </div>

                        }

                    </div>

                }

            </div>

        </div>

    );

};



// Export the socials settings component
export default RenderConnectionSocials;